<template>
  <div class="home" ref="homeContainer">
    <!-- 使用公共导航栏组件 -->
    <common-header
      :is-scrolled="isScrolled"
      :current-section="currentSection"
      @search="handleSearch"
      @lang-change="switchLang"
    />

    <!-- 轮播图部分 -->
    <div class="fullscreen-container" ref="scrollContainer">
      <section class="fullscreen-section" id="home">
        <div v-for="(banner, index) in banners" 
             :key="index" 
             class="fullscreen-slide" 
             :id="'slide-' + index">
          <div class="slide-image" :style="{ backgroundImage: `url(${banner})` }">
            <!-- 第一张图片上的标题和导航 -->
            <div v-if="index === 0" class="first-slide-content">
              <div class="slide-title">
                <h2>{{ $t('home.banner.title') }}</h2>
              </div>
              <div class="solution-nav">
                <router-link to="/solutions#unmanned" class="solution-item">
                  <span class="solution-text">{{ $t('home.banner.solutions.unmanned') }}</span>
                </router-link>
                <router-link to="/solutions#fuel" class="solution-item">
                  <span class="solution-text">{{ $t('home.banner.solutions.fuel') }}</span>
                </router-link>
                <router-link to="/solutions#unloading" class="solution-item">
                  <span class="solution-text">{{ $t('home.banner.solutions.unloading') }}</span>
                </router-link>
                <router-link to="/solutions#inspection" class="solution-item">
                  <span class="solution-text">{{ $t('home.banner.solutions.inspection') }}</span>
                </router-link>
                <router-link to="/solutions#coreTech" class="solution-item">
                  <span class="solution-text">{{ $t('home.banner.solutions.coreTech') }}</span>
                </router-link>
              </div>
            </div>
            <!-- 第二张图片上的统计数据和文字 -->
            <div v-if="index === 1" class="stats-overlay" :class="{ 'show': currentSlide === 1 }">
              <div class="stats-content">
                <h2 class="company-vision">{{ $t('home.vision') }}</h2>
                <div class="stats-grid">
                  <div class="stat-item" v-for="(stat, index) in stats" :key="index">
                    <div class="stat-label">{{ $t(`home.stats.${stat.key}.label`) }}</div>
                    <div class="stat-number">
                      <span class="counter">{{ stat.displayValue }}</span>
                      <span class="unit">{{ $t(`home.stats.${stat.key}.unit`) }}</span>
                      <span v-if="stat.suffix">{{ stat.suffix }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- 第三张图片上的统计数据和文字 -->
            <div v-if="index === 2" class="stats-overlay" :class="{ 'show': currentSlide === 2 }">
              <div class="stats-content">
                <h2 class="company-vision">{{ $t('home.rdStats.title') }}</h2>
                <div class="stats-grid rd-stats">
                  <div class="stat-item" v-for="(stat, index) in rdStats" :key="index">
                    <div class="stat-number">
                      <span class="counter">{{ stat.displayValue }}</span>
                      <span class="unit">%</span>
                    </div>
                    <div class="stat-label">{{ $t(`home.rdStats.${stat.key}`) }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 动态中心部分 -->
      <common-section type="news" id="news" style="padding: 102px 0 50px 0;">
        <div class="section-header">
          <div class="title-group">
            <h2 class="section-title">{{ $t('home.news.title') }}</h2>
            <div class="section-subtitle">{{ $t('home.news.subtitle') }}</div>
          </div>
          <div class="news-more">
            <router-link to="/news" class="more-btn">{{ $t('home.news.more') }} <i class="el-icon-arrow-right"></i></router-link>
          </div>
        </div>
        <div class="news-container">
          <div class="news-list">
            <template v-if="$t('news.list') && $t('news.list').length > 0">
              <div class="news-item" v-for="(item, index) in $t('news.list')" :key="index">
                <div class="news-image">
                  <img :src="require(`@/assets/news/${index+1}.jpg`)" :alt="item.title">
                </div>
                <div class="news-content">
                  <div class="news-date-tag">{{ item.date }}</div>
                  <h3>{{ item.title }}</h3>
                  <p class="news-desc">{{ item.desc }}</p>
                  <router-link :to="item.url" class="read-more">{{ $t('home.news.readMore') }} <i class="el-icon-arrow-right"></i></router-link>
                </div>
              </div>
            </template>
            <div v-else class="no-data">
              <p>{{ $t('common.noData') }}</p>
            </div>
          </div>
        </div>
      </common-section>

      <!-- 页脚部分 -->
      <common-section type="footer" id="contact">
        <div class="footer-container">
          <div class="footer-content">
            <div class="footer-main">
              <div class="footer-left">
                <div class="footer-logo">
                  <img src="@/assets/logo.png" alt="杭州坤时">
                </div>
                <div class="qrcode">
                  <img src="@/assets/qrcode.png" alt="智佳能二维码">
                  <p>扫码关注我们</p>
                </div>
              </div>
              <div class="footer-center">
                <div class="footer-links">
                  <div class="link-group">
                    <h4>关于坤时</h4>
                    <ul>
                      <li><a href="#" @click.prevent="scrollToAboutSection('profile')">公司简介</a></li>
                      <li><a href="#" @click.prevent="scrollToAboutSection('culture')">企业文化</a></li>
                      <li><a href="#" @click.prevent="scrollToAboutSection('history')">发展历程</a></li>
                      <li><a href="#" @click.prevent="scrollToAboutSection('honors')">荣誉资质</a></li>
                      <li><a href="#" @click.prevent="scrollToAboutSection('partners')">合作伙伴</a></li>
                    </ul>
                  </div>
                  <div class="link-group">
                    <h4>研发制造</h4>
                    <ul>
                      <li><router-link to="/rd/research">研发实力</router-link></li>
                      <li><router-link to="/rd/manufacturing">智能制造</router-link></li>
                    </ul>
                  </div>
                  <div class="link-group">
                    <h4>产品&解决方案</h4>
                    <ul>
                      <li><router-link to="/solutions/battery">化成分容解决方案</router-link></li>
                      <li><router-link to="/solutions/automation">工业自动化解决方案</router-link></li>
                      <li><router-link to="/solutions/semiconductor">半导体AMHS系统</router-link></li>
                      <li><router-link to="/solutions/solar">光伏逆变解决方案</router-link></li>
                      <li><router-link to="/solutions/wms">智能软件系统</router-link></li>
                    </ul>
                  </div>
                  <div class="link-group">
                    <h4>加入我们</h4>
                    <ul>
                      <li><router-link to="/join/career">职业发展</router-link></li>
                      <li><router-link to="/join/benefits">福利待遇</router-link></li>
                      <li><router-link to="/join/social">社会招聘</router-link></li>
                      <li><router-link to="/join/campus">校园招聘</router-link></li>
                    </ul>
                  </div>
                  <div class="link-group">
                    <h4>联系我们</h4>
                    <ul>
                      <li><router-link to="/contact/contact-info">联系方式</router-link></li>
                      <li><router-link to="/contact/feedback">留言反馈</router-link></li>
                      <li><router-link to="/contact/supplier">供应商自荐</router-link></li>
                    </ul>
                  </div>
                </div>
              </div>
              <div class="footer-right">
                <div class="service-hotline">
                  <h4>智佳能服务热线</h4>
                  <p class="phone">0755-28179806</p>
                  <p class="work-time">工作时间：周一至周五 9:00-18:00</p>
                </div>
              </div>
            </div>
          </div>
          <div class="copyright">
            <div class="copyright-content">
              <p>Copyright © 2023 杭州坤时自动化系统有限公司 ALL RIGHTS RESERVED.</p>
              <p class="icp">粤ICP备13032490号</p>
            </div>
          </div>
        </div>
      </common-section>
    </div>

    <!-- 滚动指示器 -->
    <div class="scroll-indicator" 
         :class="{ 'news-section': currentSection === 'news' }"
         @click.stop>
      <div v-for="(banner, index) in banners" 
           :key="index"
           class="dot"
           :class="{ active: currentSlide === index }"
           @click.stop="handleDotClick(index)">
      </div>
      <div class="dot" 
           :class="{ active: currentSection === 'news' }"
           @click.stop="scrollToNews">
      </div>
    </div>
  </div>
</template>

<script>
import CommonHeader from '@/components/CommonHeader.vue'
import CommonSection from '@/components/CommonSection.vue'

export default {
  name: 'HomeView',
  components: {
    CommonHeader,
    CommonSection
  },
  data() {
    return {
      isScrolled: false,
      currentSection: 'home',
      currentSlide: 0,
      isScrolling: false,
      lastScrollTime: 0,
      scrollThreshold: 50,
      banners: [
        require('@/assets/banners/1.jpg'),
        require('@/assets/banners/2.jpg'),
        require('@/assets/banners/3.jpg')
      ],
      sections: [
        { id: 'home', positions: [0, window.innerHeight, window.innerHeight * 2] },
        { id: 'news', position: window.innerHeight * 3 }, 
        { id: 'contact', position: window.innerHeight * 4 }
      ],
      currentLang: 'CN',
      searchText: '',
      activeDropdown: null,
      isHeaderHovered: false,
      yearCount: 0,
      areaCount: 0,
      employeeCount: 0,
      partnerCount: 0,
      statsTargets: {
        year: 20,
        area: 150000,
        employee: 1000,
        partner: 100
      },
      lastScrollTop: 0,
      animationKey: 0,
      isEnteringSlide: false,
      stats: [
        { key: 'founded', value: 2013, displayValue: 0, suffix: '' },
        { key: 'base', value: 200, displayValue: 0, suffix: '' },
        { key: 'employee', value: 20000, displayValue: 0, suffix: '' },
        { key: 'partner', value: 200, displayValue: 0, suffix: '' }
      ],
      rdStats: [
        { key: 'investment', value: 20, displayValue: 0 },
        { key: 'personnel', value: 50, displayValue: 0 },
        { key: 'products', value: 85, displayValue: 0 },
        { key: 'experience', value: 80, displayValue: 0 }
      ],
    }
  },
  mounted() {
    // 使用容器的滚动事件
    const container = this.$refs.scrollContainer;
    if (container) {
      container.addEventListener('scroll', this.handleScroll);
      container.addEventListener('wheel', this.handleWheel, { passive: false });
    }
  },
  beforeDestroy() {
    const container = this.$refs.scrollContainer;
    if (container) {
      container.removeEventListener('scroll', this.handleScroll);
      container.removeEventListener('wheel', this.handleWheel);
    }
  },
  watch: {
    currentSlide(newVal) {
      if (newVal === 1) {
        this.animateStats(this.stats);
      } else if (newVal === 2) {
        this.animateStats(this.rdStats);
      }
    }
  },
  methods: {
    handleDotClick(index) {
      console.log('Dot clicked:', index); // 添加调试日志
      this.scrollToSlide(index);
    },

    handleWheel(e) {
      const container = this.$refs.scrollContainer;
      if (!container) return;

      const currentScroll = container.scrollTop;
      const windowHeight = window.innerHeight;
      
      // 判断是否在轮播图区域（前三屏）
      const isInBannerSection = currentScroll < windowHeight * 3;
      
      if (isInBannerSection) {
        // 在轮播图区域时，使用原有的平滑滚动逻辑
        e.preventDefault();
        
        const now = Date.now();
        if (now - this.lastScrollTime < 100) return;
        this.lastScrollTime = now;

        this.isScrolling = true;
        const direction = e.deltaY > 0 ? 1 : -1;
        
        // 获取所有滚动位置
        const positions = this.getAllPositions();
        
        // 找到当前位置最接近的索引
        let currentIndex = positions.findIndex(pos => 
          Math.abs(currentScroll - pos) < windowHeight / 2
        );
        
        if (currentIndex === -1) {
          currentIndex = positions.findIndex(pos => pos > currentScroll) - 1;
          if (currentIndex === -1) currentIndex = 0;
        }
        
        // 计算目标索引
        let targetIndex = currentIndex + direction;
        if (targetIndex < 0) targetIndex = 0;
        if (targetIndex >= positions.length) targetIndex = positions.length - 1;

        // 滚动到目标位置
        container.scrollTo({
          top: positions[targetIndex],
          behavior: 'smooth'
        });

        // 更新当前部分
        this.updateCurrentSection(positions[targetIndex]);

        // 滚动结束后重置状态
        setTimeout(() => {
          this.isScrolling = false;
        }, 500);
      } else {
        // 在动态中心区域时，使用普通滚动
        this.updateCurrentSection(currentScroll);
      }
    },

    getAllPositions() {
      const positions = [];
      // 添加所有图片位置
      this.sections.forEach(section => {
        if (section.positions) {
          positions.push(...section.positions);
        } else if (section.position) {
          positions.push(section.position);
        }
      });
      return positions;
    },

    updateCurrentSection(scrollTop, isScrollingDown) {
      const path = this.$route.path;
      if (path === '/') {
        // 首页滚动逻辑
        for (let i = this.sections.length - 1; i >= 0; i--) {
          const section = this.sections[i];
          if (section.positions) {
            // 处理轮播图部分
            const imageIndex = section.positions.findIndex(pos => 
              Math.abs(scrollTop - pos) < window.innerHeight / 2
            );
            if (imageIndex !== -1) {
              // 只在向下滚动进入slide时触发动画
              if (isScrollingDown && !this.isEnteringSlide) {
                this.isEnteringSlide = true;
                if (imageIndex === 1) {
                  this.animateStats(this.stats);
                } else if (imageIndex === 2) {
                  this.animateStats(this.rdStats);
                }
              } else if (!isScrollingDown) {
                this.isEnteringSlide = false;
              }
              
              this.currentSlide = imageIndex;
              this.currentSection = 'home';
              return;
            }
          } else if (Math.abs(scrollTop - section.position) < window.innerHeight / 2) {
            this.currentSection = section.id;
            return;
          }
        }
      } else {
        // 其他页面的逻辑保持不变
        const routeMap = {
          '/about': 'about',
          '/rd': 'rd',
          '/solutions': 'solutions',
          '/news': 'news',
          '/join': 'join',
          '/contact': 'contact'
        };
        this.currentSection = routeMap[path] || path.split('/')[1];
      }
    },

    scrollToSlide(index) {
      const container = this.$refs.scrollContainer;
      if (!container) return;

      const positions = this.getAllPositions();
      // const windowHeight = window.innerHeight;
      
      // 判断目标位置是否在轮播图区域
      const isBannerSection = index < 3;
      
      if (isBannerSection && index >= 0 && index < positions.length) {
        container.scrollTo({
          top: positions[index],
          behavior: 'smooth'
        });
        this.updateCurrentSection(positions[index]);
      }
    },

    handleScroll() {
      const container = this.$refs.scrollContainer;
      if (!container) return;

      const scrollTop = container.scrollTop;
      const windowHeight = window.innerHeight;
      
      // 更新导航栏状态
      this.isScrolled = scrollTop >= windowHeight * 3;
      
      // 判断滚动方向
      const isScrollingDown = scrollTop > this.lastScrollTop;
      this.lastScrollTop = scrollTop;

      // 更新当前部分
      this.updateCurrentSection(scrollTop, isScrollingDown);
    },

    handleSearch(searchText) {
      if (searchText.trim()) {
        // 这里添加搜索逻辑
        console.log('Searching for:', searchText);
      }
    },

    switchLang(lang) {
      this.currentLang = lang;
      // 这里可以添加语言切换的逻辑
    },

    handleDropdownEnter(dropdownName) {
      this.activeDropdown = dropdownName;
    },
    handleDropdownLeave() {
      this.activeDropdown = null;
    },
    handleHeaderEnter() {
      this.isHeaderHovered = true;
    },
    handleHeaderLeave() {
      this.isHeaderHovered = false;
    },
    scrollToAboutSection(section) {
      // 如果当前不在关于页面，先跳转到关于页面
      if (this.$route.path !== '/about') {
        this.$router.push('/about').then(() => {
          // 等待路由跳转完成后滚动到对应部分
          this.$nextTick(() => {
            const element = document.getElementById(section);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth' });
            }
          });
        });
      } else {
        // 如果已经在关于页面，直接滚动
        const element = document.getElementById(section);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    },
    animateStats(statsArray) {
      const duration = 1000;
      const steps = 20;
      const stepDuration = duration / steps;

      statsArray.forEach((stat, index) => {
        setTimeout(() => {
          let current = 0;
          const increment = stat.value / steps;
          const interval = setInterval(() => {
            current += increment;
            if (current >= stat.value) {
              current = stat.value;
              clearInterval(interval);
            }
            stat.displayValue = Math.round(current);
          }, stepDuration);
        }, index * 50);
      });
    },
    formatNumber(num) {
      if (typeof num !== 'number' || isNaN(num)) return '0';
      return num.toLocaleString();
    },
    scrollToNews() {
      const container = this.$refs.scrollContainer;
      if (container) {
        const positions = this.getAllPositions();
        const newsPosition = positions[3]; // 新闻部分的位置是第四个位置
        
        // 直接滚动到新闻部分，不使用平滑效果
        container.scrollTop = newsPosition;
        
        this.currentSection = 'news';
        this.currentSlide = -1;
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.home {
  position: relative;
  height: 100vh;
  overflow: hidden;

  .fullscreen-container {
    height: 100vh;
    overflow-y: auto;
    scroll-behavior: auto;  // 改为 auto，移除全局平滑滚动
    scrollbar-width: none;
    -ms-overflow-style: none;
    position: relative;
    &::-webkit-scrollbar {
      display: none;
    }
  }

  .fullscreen-section {
    position: relative;
    width: 100%;
    height: auto;

    .fullscreen-slide {
      width: 100%;
      height: 100vh;
      position: relative;
      overflow: hidden;

      .slide-image {
        width: 100%;
        height: 100%;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;

        &::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.4);
          z-index: 1;
        }
      }
    }
  }

  .scroll-indicator {
    position: fixed;
    right: 30px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 9999;
    display: flex;
    flex-direction: column;
    gap: 15px;
    pointer-events: auto;
    cursor: pointer;

    .dot {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.3);
      cursor: pointer;
      transition: all 0.3s ease;
      border: 2px solid transparent;
      pointer-events: auto;
      user-select: none;
      -webkit-user-select: none;

      &.active {
        background: transparent;
        border-color: #fff;
        transform: scale(1.2);
        transition: all 0.3s ease;
      }

      &:hover {
        background: rgba(255, 255, 255, 0.5);
        transform: scale(1.1);
      }
    }

    // 当在news section时，所有点的样式
    &.news-section {
      .dot {
        background: transparent;
        border: 2px solid #333;

        &:hover {
          background: rgba(51, 51, 51, 0.3);
        }

        // 最后一个点（当前激活的news点）的样式
        &:last-child.active {
          background: #333;
          border-color: #333;
        }
      }
    }
  }

  .section-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 60px;

    .title-group {
      .section-title {
        text-align: left;
        font-size: 36px;
        color: #333;
        margin-bottom: 10px;
        position: relative;
        font-weight: 600;

        &::after {
          content: '';
          position: absolute;
          bottom: -15px;
          left: 0;
          transform: none;
          width: 60px;
          height: 3px;
          background: #409EFF;
        }
      }

      .section-subtitle {
        font-size: 18px;
        color: #666;
        margin-top: 25px;
        font-weight: 500;
      }
    }

    .news-more {
      .more-btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 12px 30px;
        background: #409EFF;
        color: #fff;
        text-decoration: none;
        border-radius: 25px;
        font-size: 16px;
        font-weight: 500;
        transition: all 0.3s ease;

        &:hover {
          background: #66b1ff;
          transform: translateY(-2px);

          i {
            transform: translateX(5px);
          }
        }
      }
    }
  }

  .news-container {
    width: 100%;
    max-width: 1519px;
    margin: 0 auto;
    padding: 0 20px;
    box-sizing: border-box;

    .news-list {
      display: grid !important;
      grid-template-columns: repeat(3, 1fr) !important;
      gap: 30px !important;
      margin-bottom: 40px !important;
      width: 100% !important;

      .news-item {
        background: #fff !important;
        border-radius: 12px !important;
        overflow: hidden !important;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
        transition: all 0.3s ease !important;
        height: 100% !important;
        display: flex !important;
        flex-direction: column !important;

        &:hover {
          transform: translateY(-5px) !important;
          box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12) !important;

          .news-image img {
            transform: scale(1.05) !important;
          }

          .read-more i {
            transform: translateX(5px) !important;
          }
        }

        .news-image {
          position: relative !important;
          width: 100% !important;
          padding-top: 66.67% !important; // 3:2 宽高比
          overflow: hidden !important;

          img {
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            width: 100% !important;
            height: 100% !important;
            object-fit: cover !important;
            transition: transform 0.3s ease !important;
          }
        }

        .news-content {
          padding: 25px !important;
          flex: 1 !important;
          display: flex !important;
          flex-direction: column !important;
          gap: 15px !important;

          .news-date-tag {
            color: #409EFF !important;
            font-size: 14px !important;
            font-weight: 500 !important;
          }

          h3 {
            font-size: 18px !important;
            color: #333 !important;
            line-height: 1.4 !important;
            font-weight: 600 !important;
            display: -webkit-box !important;
            -webkit-line-clamp: 2 !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            margin: 0 !important;
          }

          .news-desc {
            color: #666 !important;
            font-size: 15px !important;
            line-height: 1.6 !important;
            display: -webkit-box !important;
            -webkit-line-clamp: 3 !important;
            -webkit-box-orient: vertical !important;
            overflow: hidden !important;
            flex: 1 !important;
            margin: 0 !important;
          }

          .read-more {
            color: #409EFF !important;
            text-decoration: none !important;
            font-size: 15px !important;
            font-weight: 500 !important;
            display: inline-flex !important;
            align-items: center !important;
            gap: 6px !important;
            transition: all 0.3s ease !important;
            margin-top: auto !important;

            i {
              transition: transform 0.3s ease !important;
            }

            &:hover {
              color: #66b1ff !important;
            }
          }
        }
      }
    }
  }

  .footer-container {
    // ... 页脚容器样式保持不变 ...
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.home .header .header-container .nav ul li .dropdown-menu {
  background: #fff;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 0;
}

.home .header .header-container .nav ul li .dropdown-menu::before {
  border-bottom-color: #fff;
}

.home .header .header-container .nav ul li .dropdown-menu a {
  color: #333;
  border-bottom: 1px solid #e5e5e5;
  padding: 12px 25px;
}

.home .header .header-container .nav ul li .dropdown-menu a:last-child {
  border-bottom: none;
}

.home .header .header-container .nav ul li .dropdown-menu a:hover {
  background: #f5f7fa;
  color: #409EFF;
}

.stats-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.1);
  // backdrop-filter: blur(5px);
  z-index: 2;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.3s ease, visibility 0.3s ease;

  &.show {
    opacity: 1;
    visibility: visible;
  }

  .stats-content {
    width: 80%;
    text-align: center;
    max-width: 1700px;
    padding: 0 60px;

    .company-vision {
      font-size: 42px;
      color: #fff;
      margin-bottom: 40px;
    }

    .stats-grid {
      display: flex;
      justify-content: space-between;
      gap: 80px;
      margin: 0 auto;

      .stat-item {
        flex: 1;
        text-align: center;
        color: #fff;

        .stat-label {
          font-size: 24px;
          margin-bottom: 8px;
        }

        .stat-number {
          display: flex;
          align-items: baseline;
          justify-content: center;
          gap: 4px;

          .counter {
            font-size: 72px;
            font-weight: 600;
            color: #fff;
          }

          .unit {
            font-size: 36px;
            opacity: 0.9;
          }
        }
      }
    }

    .rd-stats {
      .stat-item {
        .stat-number {
          margin-bottom: 8px;
          
          .counter {
            font-size: 64px;
          }

          .unit {
            font-size: 32px;
          }
        }

        .stat-label {
          font-size: 20px;
          line-height: 1.4;
        }
      }
    }
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@media screen and (max-width: 1400px) {
  .home .news-container .news-list {
    grid-template-columns: repeat(2, 1fr) !important;
    gap: 24px !important;
    padding: 0 16px !important;
  }
}

@media screen and (max-width: 900px) {
  .home .news-container .news-list {
    grid-template-columns: 1fr !important;
    gap: 20px !important;
    padding: 0 12px !important;

    .news-item {
      .news-content {
        padding: 20px !important;
        gap: 12px !important;

        h3 {
          font-size: 17px !important;
        }

        .news-desc {
          font-size: 14px !important;
          -webkit-line-clamp: 2 !important;
        }

        .read-more {
          font-size: 14px !important;
        }
      }
    }
  }
}

@media screen and (max-width: 480px) {
  .home .news-container .news-list {
    gap: 16px !important;
    padding: 0 8px !important;

    .news-item {
      .news-image {
        padding-top: 56.25% !important; // 16:9 宽高比
      }

      .news-content {
        padding: 16px !important;
        gap: 10px !important;

        .news-date-tag {
          font-size: 13px !important;
        }

        h3 {
          font-size: 16px !important;
        }

        .news-desc {
          font-size: 13px !important;
        }

        .read-more {
          font-size: 13px !important;
        }
      }
    }
  }
}

.fullscreen-slide, .slide-image {
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
}
.slide-image img,
.fullscreen-slide img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.news-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.first-slide-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.1);
    // backdrop-filter: blur(5px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 0;
  color: #fff;
  z-index: 10;

  .slide-title {
    text-align: center;
    padding: 0 20px;
    margin-top: 20%;
    h2 {
      font-size: 48px;
      font-weight: 600;
      margin: 10px 0;
      text-shadow: 0 2px 8px rgba(0,0,0,0.3);
      opacity: 0;
      transform: translateY(30px);
      animation: slideUpFade 0.8s ease-out forwards;
    }
  }

  .solution-nav {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    display: flex;
    background: transparent;
    backdrop-filter: blur(10px);

    .solution-item {
      flex: 1;
      display: flex;
      justify-content: center;
      align-items: center;
      text-decoration: none;
      color: #fff;
      padding: 40px 10px;
      transition: all 0.3s ease;
      opacity: 0;
      transform: translateY(30px);
      animation: slideUpFade 0.8s ease-out forwards;
      animation-delay: 0.7s;
      border-radius: 15px;
      border-right: 1px solid rgba(255, 255, 255, 0.1);
      text-align: center;
      &:last-child {
        border-right: none;
      }

      &:hover {
        background: rgba($color: #D7D7D7, $alpha: 0.2);
      }

      .solution-text {
        font-size: 25px;
        font-weight: 500;
        line-height: 1.4;
        white-space: nowrap;
      }
    }
  }
}

@media (max-width: 1200px) {
  .first-slide-content {
    .slide-title {
      h2 {
        font-size: 36px;
      }
    }
    
    .solution-nav {
      .solution-item {
        padding: 15px 8px;
        .solution-text {
          font-size: 14px;
        }
      }
    }
  }
}

@media (max-width: 768px) {
  .first-slide-content {
    .slide-title {
      margin-top: 150px;
      h2 {
        font-size: 28px;
      }
    }
    
    .solution-nav {
      flex-wrap: wrap;
      
      .solution-item {
        flex: 0 0 50%;
        padding: 12px 5px;
        border-right: 1px solid rgba(255, 255, 255, 0.1);
        border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        
        &:nth-child(2n) {
          border-right: none;
        }
        
        &:nth-child(3), &:nth-child(4) {
          border-bottom: none;
        }
        
        .solution-text {
          font-size: 13px;
        }
      }
    }
  }
}

@keyframes slideUpFade {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>